import React, { useEffect, useState } from 'react';
import AbhaBadge from '../visit/AbhaBadge';
import LinkedRecordsList from '../abha/LinkedRecordsList';
import { abhaStatusMeta } from '../visit/abhaStatusMeta';
import { getABHARecord } from '../../services/api/abhaApi';
import { maskAbhaAddress } from '../../services/api/abhaProfile';
import useAbdmEnabled from '../../hooks/useAbdmEnabled';

/**
 * Patient Card ABHA block (R45b/R91): status badge, MASKED ABHA number + address
 * and the linked records. The card's patient section carries abha_status only
 * (the number lives in abdm_records), so for VERIFIED/LINKED patients the masked
 * record is read once per patient via GET /abha/record (open to all staff).
 * Loading or any error shows nothing; the number/address are never faked.
 */
export default function PatientCardAbha({ patient, isDark }) {
  const patientId = patient?.patient_id;
  const { show: abdmOn } = useAbdmEnabled();
  const hasAbha = abdmOn && ['VERIFIED', 'LINKED'].includes(abhaStatusMeta(patient?.abha_status).key);
  const [record, setRecord] = useState(null);
  const [linkedShown, setLinkedShown] = useState(false); // R130: full box replaces the masked line

  useEffect(() => {
    setRecord(null);
    if (!patientId || !hasAbha) return undefined;
    let cancelled = false;
    getABHARecord({ patientId })
      .then((r) => { if (!cancelled) setRecord(r); })
      .catch(() => { /* hide the row quietly */ });
    return () => { cancelled = true; };
  }, [patientId, hasAbha]);

  if (!patientId || !abdmOn) return null; // ABDM off: no ABHA section
  const number = linkedShown ? '' : (record?.abhaNumberMasked || '');
  const address = !linkedShown && record?.abhaAddress ? maskAbhaAddress(record.abhaAddress) : '';
  return (
    <section data-testid="patient-card-abha" className="pcard-timeline-slot">
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <AbhaBadge status={patient.abha_status} isDark={isDark} />
        {number && <span data-testid="patient-card-abha-number" style={{ fontFamily: 'monospace', fontSize: 13 }}>{number}</span>}
        {address && <span data-testid="patient-card-abha-address" style={{ fontFamily: 'monospace', fontSize: 13 }}>{address}</span>}
      </div>
      {hasAbha && <LinkedRecordsList patientId={patientId} isDark={isDark} onShowingChange={setLinkedShown} />}
    </section>
  );
}
